/**
 * Anzeige-/Übersetzungs-Helfer für den Dialogeingang.
 * Hält alle technischen → deutschen Beschriftungen zentral.
 */

import {
  DialogInboxStatus,
  ParticipantAction,
} from '../Interfaces/IDialogInbox';

// ---------- Status ----------

export const STATUS_LABEL: Record<DialogInboxStatus | 'created', string> = {
  pending: 'Offen',
  processed: 'Importiert',
  ignored: 'Ignoriert',
  created: 'Vorgang erstellt',
};

export const STATUS_COLOR: Record<
  DialogInboxStatus | 'created',
  'default' | 'success' | 'warning' | 'info'
> = {
  pending: 'warning',
  processed: 'success',
  ignored: 'default',
  created: 'success',
};

export const labelForStatus = (status?: string | null): string => {
  if (!status) return '–';
  return STATUS_LABEL[status as keyof typeof STATUS_LABEL] ?? status;
};

// ---------- Aktionen / Mandanten-Entscheidung ----------

export const ACTION_LABEL: Record<ParticipantAction, string> = {
  create: 'Mandant neu anlegen',
  use: 'Bestehenden Mandanten verwenden',
  merge: 'Datensätze zusammenführen',
  ignore: 'Ignorieren',
};

export const ACTION_LABEL_SHORT: Record<ParticipantAction, string> = {
  create: 'Neu',
  use: 'Übernommen',
  merge: 'Zusammengeführt',
  ignore: 'Ignoriert',
};

// ---------- Aktionen / Immobilien-Entscheidung ----------
// Eigene Beschriftungen für den Immobilien-/Grundstücks-Abschnitt, damit dort
// nicht die Mandanten-Texte ("Bestehenden Mandanten verwenden" o. Ä.) erscheinen.
export const ACTION_LABEL_REAL_ESTATE: Record<ParticipantAction, string> = {
  create: 'Grundstück neu anlegen',
  use: 'Bestehendes Grundstück verwenden',
  merge: 'Datensätze zusammenführen',
  ignore: 'Ignorieren',
};

// ---------- Feldnamen / Wörterbuch ----------

const FIELD_LABEL: Record<string, string> = {
  FirstName: 'Vorname',
  LastName: 'Nachname',
  SurName: 'Geburtsname',
  CompanyName: 'Firma',
  RegisterNumber: 'Handelsregister-Nr.',
  Email: 'E-Mail',
  PhoneNumber: 'Telefon',
  TaxNumber: 'Steuer-Nr.',
  DateOfBirth: 'Geburtsdatum',
  Birthdate: 'Geburtsdatum',
  Birthplace: 'Geburtsort',
  idGender_hint: 'Anrede',
  idBusinessForm_hint: 'Rechtsform',
  idLegalClientType_hint: 'Mandantentyp',
  Street: 'Straße',
  StreetNr: 'Hausnummer',
  Postcode: 'PLZ',
  City: 'Ort',
  LandRegister: 'Grundbuchblatt',
  SheetLandRegister: 'Grundbuchblattnummer',
  Area: 'Fläche',
  Price: 'Kaufpreis',
};

/**
 * Liefert die deutsche Anzeige für einen technischen Feldnamen.
 * Fällt auf einen humanisierten Default zurück, wenn nichts hinterlegt ist.
 */
export const labelForField = (key: string): string => {
  if (FIELD_LABEL[key]) return FIELD_LABEL[key];
  // Heuristik: snake/camelCase → mit Leerzeichen
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^[a-z]/, c => c.toUpperCase());
};

// ---------- Datum ----------

const DE_DATE = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const DE_DATETIME = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Formatiert ISO/Datum-Werte als dd.mm.yyyy.
 * Liefert "–" bei leeren Werten und gibt den Originalwert zurück, wenn das
 * Parsen scheitert (so dass Eingaben wie "1985" nicht "Invalid Date" werden).
 */
export const formatGermanDate = (
  value?: string | null,
  withTime: boolean = false
): string => {
  if (!value) return '–';
  const d = new Date(value);
  if (isNaN(d.getTime())) {
    // Fallback: nimm "YYYY-MM-DD" und drehe es manuell, sonst einfach Original.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    return value;
  }
  return withTime ? DE_DATETIME.format(d) : DE_DATE.format(d);
};

// ---------- Kontext-Werte ----------

/**
 * Wandelt Werte für die Anzeige im Detail-Dialog. Erkennt Datumsmuster
 * (ISO oder yyyy-mm-dd) und formatiert sie deutsch; alles andere bleibt.
 */
export const formatFieldValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return '–';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  if (
    /(date|geburts|datum)/i.test(key) &&
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}/.test(value)
  ) {
    return formatGermanDate(value);
  }
  return String(value);
};
